"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { coordKey, type HexDirection } from "../../hex-path/hex-grid";
import type { HexCoord } from "../../hex-path/types";
import {
	HEX_APPROACH_FACING_DURATION_MS,
	HEX_APPROACH_FACING_EPS_RAD,
	normalizeFacingRad,
	shortestFacingDeltaRad,
} from "../lib/hex-character-facing";
import type { HexagonLayoutMetrics } from "../lib/hexagon-layout-metrics";
import { hexCellCenter } from "../lib/hexagon-layout-metrics";
import {
	DEFAULT_CHARACTER_MOVEMENT_MS_PER_TILE,
	facingRadForHexDirection,
	type PathCharacterPlayerIcons,
	resolveCharacterStepDurationMs,
	resolveFacingForStep,
	resolvePlayerIconForStep,
} from "../lib/path-character-movement";

/** Idle locomotion band; keep last facing. */
const IDLE_SPEED_SCALE = 0;
/** Click-to-path default band (matches scene WALK). */
const WALK_SPEED_SCALE = 0.65;

/**
 * Max turn rate while easing toward `targetFacingRad` (π rad / approach ms).
 * Continuous — never restarts a fixed-duration tween per stick sample.
 */
const FACING_TURN_RAD_PER_MS = Math.PI / HEX_APPROACH_FACING_DURATION_MS;

interface CharacterDisplayCenter {
	left: number;
	top: number;
}

export interface CharacterMovementDisplaySnapshot {
	center: CharacterDisplayCenter | null;
	playerIcon: string;
	isMoving: boolean;
	stepTransitionMs: number;
	activeStepFrom: HexCoord | null;
	activeStepTo: HexCoord | null;
	facingDirection?: HexDirection;
	/** Radians; 0 = screen-up. */
	facingRad?: number;
	/**
	 * 0 idle; (0,0.45] sneak; (0.45,0.85] walk; else run — same bands as
	 * scene {@link resolveLocomotionBodyClipKey}.
	 */
	speedScale?: number;
}

interface UseHexagonMapCharacterMovementOptions {
	anchorCoord: HexCoord | null;
	anchorSyncKey?: string | null;
	nextAnchorCoord: HexCoord | null;
	metrics: HexagonLayoutMetrics;
	playerIcons: PathCharacterPlayerIcons;
	msPerTile?: number;
	reducedMotion?: boolean;
	onMovementComplete?: (coord: HexCoord) => void;
}

function lerpDisplayCenter(
	from: CharacterDisplayCenter,
	to: CharacterDisplayCenter,
	t: number,
): CharacterDisplayCenter {
	return {
		left: from.left + (to.left - from.left) * t,
		top: from.top + (to.top - from.top) * t,
	};
}

interface CharacterMovementOptions {
	stepDurationMs?: number;
	/**
	 * Locomotion band while translating (sneak/walk/run). Defaults to WALK
	 * for click-to-path; thumb passes analog scale.
	 */
	speedScale?: number;
	/**
	 * Visual start for the first step (interrupt mid-tile). Defaults to the
	 * `from` tile center.
	 */
	visualFromCenter?: CharacterDisplayCenter;
}

interface ActiveStep {
	route: HexCoord[];
	stepIndex: number;
	stepStartedAt: number;
	stepDurationMs: number;
	fromCenter: CharacterDisplayCenter;
	toCenter: CharacterDisplayCenter;
	locoSpeedScale: number;
	generation: number;
}

interface UseHexagonMapCharacterMovementResult {
	displayCenter: CharacterDisplayCenter | null;
	playerIcon: string;
	isMoving: boolean;
	playerCoord: HexCoord | null;
	/** Tile used for pathfinding — current step destination while moving, else resting coord. */
	resolvePathOriginCoord: () => HexCoord | null;
	startMovement: (
		route: HexCoord[],
		options?: CharacterMovementOptions,
	) => boolean;
	setFacingForStep: (from: HexCoord, to: HexCoord) => void;
	/** Continuous 360° facing target (thumb stick); eases in the mover rAF. */
	setFacingRad: (facingRad: number) => void;
	snapToCoord: (coord: HexCoord) => void;
	/**
	 * Stop translation but keep the current visual center. Updates logical
	 * coord to `coord` (or nearest hold) without teleporting the doll.
	 */
	cancelMovementKeepingVisual: (logicalCoord?: HexCoord | null) => void;
	cancelMovement: () => void;
	/**
	 * Commit logical tile without moving the visual center (interrupt helper).
	 */
	commitLogicalCoord: (coord: HexCoord) => void;
	/**
	 * Live retarget hop duration / loco band without restarting the step
	 * (preserves progress t). Used when stick magnitude changes mid-hop.
	 */
	retargetActiveStepLocomotion: (input: {
		speedScale: number;
		stepDurationMs: number;
	}) => void;
	stepTransitionMs: number;
	subscribeDisplay: (listener: () => void) => () => void;
	getDisplaySnapshot: () => CharacterMovementDisplaySnapshot;
}

/** Idle: layout-derived resting center. Moving: rAF-lerped display center. */
export function resolveCharacterRenderCenter(
	display: CharacterMovementDisplaySnapshot,
	restingCenter: CharacterDisplayCenter | null,
): CharacterDisplayCenter | null {
	if (display.center != null) {
		return display.center;
	}
	return restingCenter;
}

function resolveInitialDisplaySnapshot(
	anchorCoord: HexCoord | null,
	nextAnchorCoord: HexCoord | null,
	metrics: HexagonLayoutMetrics,
	playerIcons: PathCharacterPlayerIcons,
): CharacterMovementDisplaySnapshot {
	const facing =
		nextAnchorCoord && anchorCoord
			? resolveFacingForStep(anchorCoord, nextAnchorCoord)
			: {
					facingDirection: "right" as const,
					facingRad: facingRadForHexDirection("right"),
				};
	return {
		center: anchorCoord
			? hexCellCenter(anchorCoord.row, anchorCoord.col, metrics)
			: null,
		playerIcon:
			nextAnchorCoord && anchorCoord
				? resolvePlayerIconForStep(
						anchorCoord,
						nextAnchorCoord,
						playerIcons,
					)
				: playerIcons.right,
		isMoving: false,
		stepTransitionMs: 0,
		activeStepFrom: null,
		activeStepTo: null,
		facingDirection: facing.facingDirection,
		facingRad: facing.facingRad,
		speedScale: IDLE_SPEED_SCALE,
	};
}

function easeFacingToward(
	currentRad: number,
	targetRad: number,
	dtMs: number,
	reducedMotion: boolean,
): number {
	const from = normalizeFacingRad(currentRad);
	const to = normalizeFacingRad(targetRad);
	const delta = shortestFacingDeltaRad(from, to);
	if (reducedMotion || Math.abs(delta) < HEX_APPROACH_FACING_EPS_RAD) {
		return to;
	}
	const maxStep = FACING_TURN_RAD_PER_MS * Math.max(0, dtMs);
	if (Math.abs(delta) <= maxStep) {
		return to;
	}
	return normalizeFacingRad(from + Math.sign(delta) * maxStep);
}

export function useHexagonMapCharacterMovement(
	options: UseHexagonMapCharacterMovementOptions,
): UseHexagonMapCharacterMovementResult {
	const {
		anchorCoord,
		anchorSyncKey,
		nextAnchorCoord,
		metrics,
		playerIcons,
		msPerTile = DEFAULT_CHARACTER_MOVEMENT_MS_PER_TILE,
		reducedMotion = false,
		onMovementComplete,
	} = options;

	const [playerCoord, setPlayerCoord] = useState<HexCoord | null>(
		anchorCoord,
	);
	const [isMoving, setIsMoving] = useState(false);
	const displaySnapshotRef = useRef<CharacterMovementDisplaySnapshot>(
		resolveInitialDisplaySnapshot(
			anchorCoord,
			nextAnchorCoord,
			metrics,
			playerIcons,
		),
	);
	const displayListenersRef = useRef(new Set<() => void>());
	const moverRafRef = useRef<number | null>(null);
	const lastMoverNowRef = useRef<number | null>(null);
	const activeStepRef = useRef<ActiveStep | null>(null);
	const targetFacingRadRef = useRef(
		normalizeFacingRad(
			displaySnapshotRef.current.facingRad ??
				facingRadForHexDirection("right"),
		),
	);
	const movementGenerationRef = useRef(0);
	const inFlightToRef = useRef<HexCoord | null>(null);
	const lastSyncedAnchorKeyRef = useRef<string | null>(null);
	const holdPlayerCoordUntilAnchorRef = useRef<HexCoord | null>(null);
	/**
	 * When true, idle/hold sync must not rewrite `display.center` to a tile
	 * center (mid-tile cancel / redirect). Cleared by finish/snap/start.
	 */
	const preserveVisualCenterRef = useRef(false);
	const playerCoordRef = useRef<HexCoord | null>(anchorCoord);
	const onMovementCompleteRef = useRef(onMovementComplete);
	const reducedMotionRef = useRef(reducedMotion);
	const metricsRef = useRef(metrics);
	const playerIconsRef = useRef(playerIcons);
	playerCoordRef.current = playerCoord;
	onMovementCompleteRef.current = onMovementComplete;
	reducedMotionRef.current = reducedMotion;
	metricsRef.current = metrics;
	playerIconsRef.current = playerIcons;

	const stepDurationMs = resolveCharacterStepDurationMs(msPerTile);

	const publishDisplay = useCallback(() => {
		for (const listener of displayListenersRef.current) {
			listener();
		}
	}, []);

	const patchDisplay = useCallback(
		(partial: Partial<CharacterMovementDisplaySnapshot>) => {
			displaySnapshotRef.current = {
				...displaySnapshotRef.current,
				...partial,
			};
		},
		[],
	);

	const publish = useCallback(() => {
		publishDisplay();
	}, [publishDisplay]);

	const subscribeDisplay = useCallback((listener: () => void) => {
		displayListenersRef.current.add(listener);
		return () => {
			displayListenersRef.current.delete(listener);
		};
	}, []);

	const getDisplaySnapshot = useCallback(
		() => displaySnapshotRef.current,
		[],
	);

	const stopMoverLoop = useCallback(() => {
		if (moverRafRef.current != null) {
			cancelAnimationFrame(moverRafRef.current);
			moverRafRef.current = null;
		}
		lastMoverNowRef.current = null;
	}, []);

	const finishAt = useCallback(
		(destination: HexCoord, previousStep?: HexCoord) => {
			activeStepRef.current = null;
			inFlightToRef.current = null;
			preserveVisualCenterRef.current = false;
			holdPlayerCoordUntilAnchorRef.current = destination;
			playerCoordRef.current = destination;
			setIsMoving(false);
			setPlayerCoord(destination);
			const finishFacing = previousStep
				? resolveFacingForStep(previousStep, destination)
				: null;
			if (finishFacing) {
				targetFacingRadRef.current = finishFacing.facingRad;
			}
			patchDisplay({
				center: hexCellCenter(
					destination.row,
					destination.col,
					metricsRef.current,
				),
				isMoving: false,
				stepTransitionMs: 0,
				activeStepFrom: null,
				activeStepTo: null,
				speedScale: IDLE_SPEED_SCALE,
				...(previousStep
					? {
							playerIcon: resolvePlayerIconForStep(
								previousStep,
								destination,
								playerIconsRef.current,
							),
						}
					: {}),
				...(finishFacing
					? {
							facingDirection: finishFacing.facingDirection,
							facingRad: finishFacing.facingRad,
						}
					: {}),
			});
			publish();
			onMovementCompleteRef.current?.(destination);
		},
		[patchDisplay, publish],
	);

	const beginStep = useCallback(
		(
			route: HexCoord[],
			stepIndex: number,
			stepDuration: number,
			locoSpeedScale: number,
			generation: number,
			visualFrom?: CharacterDisplayCenter,
		) => {
			const from = route[stepIndex];
			const to = route[stepIndex + 1];
			if (!from || !to) {
				return false;
			}
			const metricsNow = metricsRef.current;
			const fromCenter = hexCellCenter(from.row, from.col, metricsNow);
			const toCenter = hexCellCenter(to.row, to.col, metricsNow);
			const stepFacing = resolveFacingForStep(from, to);
			const startCenter = visualFrom ?? fromCenter;

			inFlightToRef.current = to;
			playerCoordRef.current = from;
			setPlayerCoord(from);
			targetFacingRadRef.current = stepFacing.facingRad;

			activeStepRef.current = {
				route,
				stepIndex,
				stepStartedAt: performance.now(),
				stepDurationMs: Math.max(16, stepDuration),
				fromCenter: startCenter,
				toCenter,
				locoSpeedScale,
				generation,
			};

			patchDisplay({
				playerIcon: resolvePlayerIconForStep(
					from,
					to,
					playerIconsRef.current,
				),
				center: startCenter,
				isMoving: true,
				stepTransitionMs: stepDuration,
				activeStepFrom: from,
				activeStepTo: to,
				facingDirection: stepFacing.facingDirection,
				speedScale: locoSpeedScale,
			});
			return true;
		},
		[patchDisplay],
	);

	const ensureMoverLoop = useCallback(() => {
		if (moverRafRef.current != null) {
			return;
		}
		const tick = (now: number) => {
			moverRafRef.current = null;
			const prevNow = lastMoverNowRef.current;
			lastMoverNowRef.current = now;
			const dtMs =
				prevNow == null ? 16 : Math.min(48, Math.max(0, now - prevNow));

			const snap = displaySnapshotRef.current;
			let facingRad = normalizeFacingRad(
				snap.facingRad ?? targetFacingRadRef.current,
			);
			facingRad = easeFacingToward(
				facingRad,
				targetFacingRadRef.current,
				dtMs,
				reducedMotionRef.current,
			);

			let center = snap.center;
			let stillMoving = false;
			/** finishAt / beginStep already patched (+ finishAt published). */
			let stepTransitioned = false;
			const step = activeStepRef.current;

			if (step && step.generation === movementGenerationRef.current) {
				stillMoving = true;
				const duration = Math.max(16, step.stepDurationMs);
				const t = Math.min(1, (now - step.stepStartedAt) / duration);
				center = lerpDisplayCenter(step.fromCenter, step.toCenter, t);

				if (t >= 1) {
					const from = step.route[step.stepIndex];
					const to = step.route[step.stepIndex + 1];
					if (!from || !to) {
						activeStepRef.current = null;
						stillMoving = false;
					} else if (step.stepIndex + 1 >= step.route.length - 1) {
						finishAt(to, from);
						stillMoving = false;
						stepTransitioned = true;
						center = displaySnapshotRef.current.center;
						facingRad = normalizeFacingRad(
							displaySnapshotRef.current.facingRad ?? facingRad,
						);
					} else {
						const ok = beginStep(
							step.route,
							step.stepIndex + 1,
							step.stepDurationMs,
							step.locoSpeedScale,
							step.generation,
						);
						stepTransitioned = true;
						if (ok && activeStepRef.current) {
							stillMoving = true;
							center = activeStepRef.current.fromCenter;
						} else {
							stillMoving = false;
						}
					}
				}
			} else if (step) {
				activeStepRef.current = null;
			}

			const facingSettled =
				Math.abs(
					shortestFacingDeltaRad(
						facingRad,
						targetFacingRadRef.current,
					),
				) < HEX_APPROACH_FACING_EPS_RAD;

			if (stepTransitioned && !stillMoving) {
				// finishAt already published idle state; keep facing ease only.
				if (!facingSettled) {
					patchDisplay({ facingRad });
					publish();
				}
			} else if (stillMoving) {
				patchDisplay({ center, facingRad });
				publish();
			} else if (activeStepRef.current == null) {
				patchDisplay({
					center,
					facingRad,
					isMoving: false,
				});
				publish();
			} else {
				patchDisplay({ center, facingRad });
				publish();
			}

			const needsFacing = !facingSettled;
			const needsMove = activeStepRef.current != null;
			if (needsFacing || needsMove) {
				moverRafRef.current = requestAnimationFrame(tick);
			} else {
				lastMoverNowRef.current = null;
			}
		};
		moverRafRef.current = requestAnimationFrame(tick);
	}, [beginStep, finishAt, patchDisplay, publish]);

	useEffect(() => {
		return () => {
			stopMoverLoop();
		};
	}, [stopMoverLoop]);

	useEffect(() => {
		if (isMoving) {
			return;
		}

		const anchorKey =
			anchorSyncKey ??
			(anchorCoord ? coordKey(anchorCoord.row, anchorCoord.col) : null);
		const heldCoord = holdPlayerCoordUntilAnchorRef.current;
		if (anchorKey === lastSyncedAnchorKeyRef.current) {
			if (heldCoord) {
				const anchorMatchesHold =
					anchorCoord != null &&
					coordKey(anchorCoord.row, anchorCoord.col) ===
						coordKey(heldCoord.row, heldCoord.col);
				if (!anchorMatchesHold) {
					setPlayerCoord(heldCoord);
					if (!preserveVisualCenterRef.current) {
						patchDisplay({
							center: hexCellCenter(
								heldCoord.row,
								heldCoord.col,
								metrics,
							),
						});
						publish();
					}
				}
			}
			return;
		}

		if (heldCoord) {
			const anchorMatchesHold =
				anchorCoord != null &&
				coordKey(anchorCoord.row, anchorCoord.col) ===
					coordKey(heldCoord.row, heldCoord.col);
			if (!anchorMatchesHold) {
				setPlayerCoord(heldCoord);
				if (!preserveVisualCenterRef.current) {
					patchDisplay({
						center: hexCellCenter(
							heldCoord.row,
							heldCoord.col,
							metrics,
						),
					});
					publish();
				}
				return;
			}
			holdPlayerCoordUntilAnchorRef.current = null;
		}

		lastSyncedAnchorKeyRef.current = anchorKey;

		setPlayerCoord(anchorCoord);
		if (!anchorCoord) {
			targetFacingRadRef.current = facingRadForHexDirection("right");
			patchDisplay({
				center: null,
				playerIcon: playerIcons.right,
				isMoving: false,
				stepTransitionMs: 0,
				facingDirection: "right",
				facingRad: facingRadForHexDirection("right"),
				speedScale: IDLE_SPEED_SCALE,
			});
			publish();
			return;
		}

		const nextCenter = hexCellCenter(
			anchorCoord.row,
			anchorCoord.col,
			metrics,
		);
		const nextFacing = nextAnchorCoord
			? resolveFacingForStep(anchorCoord, nextAnchorCoord)
			: null;
		const nextPlayerIcon = nextAnchorCoord
			? resolvePlayerIconForStep(
					anchorCoord,
					nextAnchorCoord,
					playerIcons,
				)
			: playerIcons.right;

		if (nextFacing) {
			targetFacingRadRef.current = nextFacing.facingRad;
		}
		patchDisplay({
			center: nextCenter,
			playerIcon: nextPlayerIcon,
			isMoving: false,
			stepTransitionMs: 0,
			speedScale: IDLE_SPEED_SCALE,
			...(nextFacing
				? {
						facingDirection: nextFacing.facingDirection,
						facingRad: nextFacing.facingRad,
					}
				: {}),
		});
		publish();
	}, [
		anchorCoord,
		anchorSyncKey,
		isMoving,
		metrics,
		nextAnchorCoord,
		patchDisplay,
		playerIcons,
		publish,
	]);

	useEffect(() => {
		if (isMoving) {
			return;
		}
		if (holdPlayerCoordUntilAnchorRef.current) {
			return;
		}
		if (preserveVisualCenterRef.current) {
			return;
		}
		const coord = anchorCoord ?? playerCoordRef.current;
		if (!coord) {
			return;
		}
		patchDisplay({
			center: hexCellCenter(coord.row, coord.col, metrics),
		});
		publish();
	}, [anchorCoord, isMoving, metrics, patchDisplay, publish]);

	const resolvePathOriginCoord = useCallback((): HexCoord | null => {
		return (
			inFlightToRef.current ??
			holdPlayerCoordUntilAnchorRef.current ??
			playerCoordRef.current
		);
	}, []);

	const cancelMovement = useCallback(() => {
		stopMoverLoop();
		movementGenerationRef.current += 1;
		activeStepRef.current = null;
		inFlightToRef.current = null;
		preserveVisualCenterRef.current = false;
		setIsMoving(false);
		patchDisplay({
			isMoving: false,
			stepTransitionMs: 0,
			activeStepFrom: null,
			activeStepTo: null,
			speedScale: IDLE_SPEED_SCALE,
		});
		publish();
	}, [patchDisplay, publish, stopMoverLoop]);

	const commitLogicalCoord = useCallback((coord: HexCoord) => {
		holdPlayerCoordUntilAnchorRef.current = coord;
		playerCoordRef.current = coord;
		setPlayerCoord(coord);
	}, []);

	const cancelMovementKeepingVisual = useCallback(
		(logicalCoord?: HexCoord | null) => {
			stopMoverLoop();
			movementGenerationRef.current += 1;
			activeStepRef.current = null;
			inFlightToRef.current = null;
			preserveVisualCenterRef.current = true;
			setIsMoving(false);
			if (logicalCoord) {
				commitLogicalCoord(logicalCoord);
			}
			patchDisplay({
				isMoving: false,
				stepTransitionMs: 0,
				activeStepFrom: null,
				activeStepTo: null,
				speedScale: IDLE_SPEED_SCALE,
			});
			publish();
		},
		[commitLogicalCoord, patchDisplay, publish, stopMoverLoop],
	);

	const snapToCoord = useCallback(
		(coord: HexCoord) => {
			stopMoverLoop();
			movementGenerationRef.current += 1;
			activeStepRef.current = null;
			inFlightToRef.current = null;
			preserveVisualCenterRef.current = false;
			commitLogicalCoord(coord);
			setIsMoving(false);
			patchDisplay({
				center: hexCellCenter(coord.row, coord.col, metrics),
				isMoving: false,
				stepTransitionMs: 0,
				activeStepFrom: null,
				activeStepTo: null,
				speedScale: IDLE_SPEED_SCALE,
			});
			publish();
		},
		[commitLogicalCoord, metrics, patchDisplay, publish, stopMoverLoop],
	);

	const setFacingRad = useCallback(
		(facingRad: number) => {
			targetFacingRadRef.current = normalizeFacingRad(facingRad);
			ensureMoverLoop();
		},
		[ensureMoverLoop],
	);

	const retargetActiveStepLocomotion = useCallback(
		(input: { speedScale: number; stepDurationMs: number }) => {
			const step = activeStepRef.current;
			if (
				!step ||
				step.generation !== movementGenerationRef.current ||
				!displaySnapshotRef.current.isMoving
			) {
				return;
			}
			const speedScale = Math.min(1, Math.max(0, input.speedScale));
			const newDuration = Math.max(16, input.stepDurationMs);
			const now = performance.now();
			const oldDuration = Math.max(16, step.stepDurationMs);
			const t = Math.min(
				1,
				Math.max(0, (now - step.stepStartedAt) / oldDuration),
			);
			step.stepDurationMs = newDuration;
			step.stepStartedAt = now - t * newDuration;
			step.locoSpeedScale = speedScale;
			const prevScale = displaySnapshotRef.current.speedScale ?? 0;
			if (Math.abs(prevScale - speedScale) > 0.02) {
				patchDisplay({
					speedScale,
					stepTransitionMs: newDuration,
				});
				publish();
			}
		},
		[patchDisplay, publish],
	);

	const setFacingForStep = useCallback(
		(from: HexCoord, to: HexCoord) => {
			const nextIcon = resolvePlayerIconForStep(
				from,
				to,
				playerIconsRef.current,
			);
			const nextFacing = resolveFacingForStep(from, to);
			patchDisplay({
				playerIcon: nextIcon,
				facingDirection: nextFacing.facingDirection,
			});
			publish();
			setFacingRad(nextFacing.facingRad);
		},
		[patchDisplay, publish, setFacingRad],
	);

	const startMovement = useCallback(
		(route: HexCoord[], options?: CharacterMovementOptions): boolean => {
			if (!route.length) {
				return false;
			}

			/**
			 * Ignore duplicate single-hop restarts (thumb complete-chain +
			 * drive sample can both fire in one tick). Restarting bumps
			 * generation and teleports the doll back to the step origin.
			 */
			const active = activeStepRef.current;
			const snap = displaySnapshotRef.current;
			if (
				active &&
				snap.isMoving &&
				route.length === 2 &&
				active.route.length >= 2
			) {
				const wantFrom = route[0];
				const wantTo = route[1];
				const curFrom = active.route[active.stepIndex];
				const curTo = active.route[active.stepIndex + 1];
				if (
					wantFrom &&
					wantTo &&
					curFrom &&
					curTo &&
					wantFrom.row === curFrom.row &&
					wantFrom.col === curFrom.col &&
					wantTo.row === curTo.row &&
					wantTo.col === curTo.col
				) {
					return true;
				}
			}

			const locoSpeedScale = (() => {
				if (
					typeof options?.speedScale === "number" &&
					Number.isFinite(options.speedScale)
				) {
					return Math.min(1, Math.max(0, options.speedScale));
				}
				if (
					typeof options?.stepDurationMs === "number" &&
					options.stepDurationMs > 0 &&
					msPerTile > 0
				) {
					return Math.min(
						1,
						Math.max(0.1, msPerTile / options.stepDurationMs),
					);
				}
				return WALK_SPEED_SCALE;
			})();
			const effectiveStepDurationMs =
				options?.stepDurationMs ?? stepDurationMs;

			stopMoverLoop();
			movementGenerationRef.current += 1;
			const generation = movementGenerationRef.current;
			activeStepRef.current = null;
			inFlightToRef.current = null;
			preserveVisualCenterRef.current = false;

			if (route.length === 1) {
				const onlyStep = route[0];
				if (!onlyStep) {
					return false;
				}
				finishAt(onlyStep);
				return true;
			}

			if (reducedMotion) {
				const destination = route[route.length - 1];
				if (!destination) {
					return false;
				}
				finishAt(destination, route[route.length - 2] ?? undefined);
				return true;
			}

			setIsMoving(true);
			const visualFrom =
				options?.visualFromCenter ??
				displaySnapshotRef.current.center ??
				undefined;
			const ok = beginStep(
				route,
				0,
				effectiveStepDurationMs,
				locoSpeedScale,
				generation,
				visualFrom ?? undefined,
			);
			if (!ok) {
				setIsMoving(false);
				return false;
			}
			publish();
			ensureMoverLoop();
			return true;
		},
		[
			beginStep,
			ensureMoverLoop,
			finishAt,
			msPerTile,
			publish,
			reducedMotion,
			stepDurationMs,
			stopMoverLoop,
		],
	);

	const displaySnapshot = displaySnapshotRef.current;

	return {
		displayCenter: displaySnapshot.center,
		playerIcon: displaySnapshot.playerIcon,
		isMoving,
		playerCoord,
		resolvePathOriginCoord,
		startMovement,
		setFacingForStep,
		setFacingRad,
		snapToCoord,
		cancelMovementKeepingVisual,
		cancelMovement,
		commitLogicalCoord,
		retargetActiveStepLocomotion,
		stepTransitionMs: isMoving ? stepDurationMs : 0,
		subscribeDisplay,
		getDisplaySnapshot,
	};
}
